// ABOUTME: Job overview section for the file an attempt consumed and the file it generated.
// ABOUTME: The job attachment downloads through the attachment endpoint, older files by name.

import { Box, Button, FileDownloadIcon, Typography } from '@ds/coolshop';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { downloadFile } from '@src/utils/hierarchies/downloadFile';
import { useIntl } from 'react-intl';

import { downloadJobAttachment, jobApiErrorMessage } from '../../../../api/jobApi';
import { JobResultView } from '../../../../types';
import { SectionCard } from './SectionCard';

interface IJobFilesSectionProps {
    job: JobResultView;
}

/**
 * The file the job consumed, as recorded in its data. The `media` and `imports` buckets resolve
 * to the same directory, so an uploaded file is served by /api/download/:type/:file just like a
 * generated one. Cron-launched CAT imports read straight from a mounted folder and are moved
 * away afterwards, so they record no downloadable input.
 */
const getInputFileName = (job: JobResultView) => {
    const data = job.data as { filename?: string } | undefined;
    return typeof data?.filename === 'string' && data.filename ? data.filename : undefined;
};

export function JobFilesSection(props: Readonly<IJobFilesSectionProps>) {
    const { job } = props;
    const intl = useIntl();

    // /api/download/:type/:file resolves any job type — isImport/isExport only pick the
    // bucket and the courtesy filename — so a filename is enough to offer the download.
    const outputFileName = job.filename;
    const inputFileName = getInputFileName(job);
    const { attachmentJobId } = job;

    if (!outputFileName && !inputFileName) {
        return null;
    }

    const downloadOutput = (fileName: string) => {
        if (!attachmentJobId) {
            downloadFile(fileName, job.type);
            return;
        }
        downloadJobAttachment(attachmentJobId, fileName).catch(error =>
            SnackBarUtils.error(jobApiErrorMessage(error)),
        );
    };

    return (
        <SectionCard
            title={intl.formatMessage({
                id: 'job.section.files.title',
                defaultMessage: 'Files',
            })}
        >
            {inputFileName && (
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.formatMessage({
                            id: 'job.section.files.input',
                            defaultMessage: 'Imported file',
                        })}
                    </Typography>
                    <Button
                        startIcon={<FileDownloadIcon />}
                        onClick={() => downloadFile(inputFileName, job.type)}
                    >
                        {intl.formatMessage({
                            id: 'export-job.download-label',
                            defaultMessage: 'download file',
                        })}
                    </Button>
                </Box>
            )}
            {outputFileName && (
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.formatMessage({
                            id: 'job.section.files.output',
                            defaultMessage: 'Generated file',
                        })}
                    </Typography>
                    <Button
                        startIcon={<FileDownloadIcon />}
                        onClick={() => downloadOutput(outputFileName)}
                    >
                        {intl.formatMessage({
                            id: 'export-job.download-label',
                            defaultMessage: 'download file',
                        })}
                    </Button>
                </Box>
            )}
        </SectionCard>
    );
}
